/**
 * This is currently a general purpose utility file for interacting with
 * different data models and objects that are returned by the API.
 *
 * As such it may make more sense for this to move to `api/utils.js`
 *
 * @flow
 */

import React from 'react';

import type { Offer, Card, FamilyAccounts, PaymentMethod } from '../api/types';

import CardIconAmex from '../assets/icons/card-amex-svg';
import CardIconDiscover from '../assets/icons/card-discover-svg';
import CardIconMasterCard from '../assets/icons/card-mastercard-svg';
import CardIconVisa from '../assets/icons/card-visa-svg';
import { emptyArray } from './constants';
import { sortBy } from './';

export const offerOptions = (offer: ?Offer) => {
  if (offer && offer.formatted && offer.formatted.offeroptions) {
    return offer.formatted.offeroptions;
  }
  return emptyArray;
};

// credit card related things

// these constants are copies of what is returned by the API
export const AMEX = 'americanexpress';
export const DISCOVER = 'discover';
export const MASTERCARD = 'mastercard';
export const VISA = 'visa';

type CardType = typeof AMEX | typeof DISCOVER | typeof MASTERCARD | typeof VISA;

// TODO: this could/should be grabbed from the API
export const cardType: (cardNumber: string) => CardType | null = (
  cardNumber: string
) => {
  if (new RegExp('^(?:3[47][0-9]{13})$').test(cardNumber)) {
    return AMEX;
  }
  if (new RegExp('^(6(?:011|5[0-9]{2})[0-9]{12})$').test(cardNumber)) {
    return DISCOVER;
  }
  if (new RegExp('^(?:(5[1-5]|2[2-7])[0-9]{14})$').test(cardNumber)) {
    return MASTERCARD;
  }
  if (new RegExp('^(?:4(([0-9]{12})|([0-9]{15})))$').test(cardNumber)) {
    return VISA;
  }
  return null;
};

export const cardIcon = (cardType: string | null) => {
  switch (cardType && cardType.toLowerCase()) {
    case AMEX:
      return <CardIconAmex />;
    case DISCOVER:
      return <CardIconDiscover />;
    case MASTERCARD:
      return <CardIconMasterCard />;
    case VISA:
      return <CardIconVisa />;
    default:
      return null;
  }
};

export const cardTail = (card: ?Card) => {
  let prefix = '**** **** **** ';
  const tail = (card && card.tail) || '????';
  if (card && card.cardtype && card.cardtype.toLowerCase() === AMEX) {
    prefix = '**** ****** *';
  }
  return `${prefix}${tail}`;
};

// ensures the input is at least two characters in length by padding the left
// with a 0
const leftPad = (v: string | number | void | null) => {
  if (typeof v === 'string') {
    return v.length === 1 ? `0${v}` : v;
  } else if (typeof v === 'number') {
    return v > 0 && v < 10 ? `0${v}` : `${v}`;
  }
  return '??';
};

export const cardExpiration = (card: ?Card) => {
  const month = leftPad(card && card.expmonth);
  const year = leftPad(card && card.expyear);
  return `${month}/${year}`;
};

// UserData-style results

const toDate = v => new Date(v || '2000-01-01 00:00:00');
const byDateJoined = sortBy('date_joined', toDate);
export const sortOwnedMembers = (data: FamilyAccounts) => ({
  ...data,
  owned: data.owned.map(o => ({
    ...o,
    members: {
      ...o.members,
      active: o.members.active.slice(0).sort(byDateJoined),
    },
  })),
});

export const defaultPaymentMethods = (data: PaymentMethod): PaymentMethod => ({
  cards: data.cards || emptyArray,
  zonecash: (data.zonecash || emptyArray).map(z => ({
    ...z,
    balanceincents: z.balanceincents || 0,
    refillamountincents: z.refillamountincents || 0,
    autorefill: z.autorefill || false,
    balance: z.balance || { balance: '', refillamount: '' },
  })),
});

export const defaultUserData = (data: *): * => ({
  emailaddress: data.emailaddress,
  familyaccounts: sortOwnedMembers(data.familyaccounts),
  firstname: data.firstname,
  hasoffers: data.hasoffers || false,
  hasprofileinfo: data.hasprofileinfo || false,
  paymentmethods: defaultPaymentMethods(data.paymentmethods || emptyArray),
  phonenumber: data.phonenumber || null,
  get fleetAccount() {
    const { familyaccounts } = this;
    if (familyaccounts && familyaccounts.owned.length) {
      return familyaccounts.owned[0];
    }
    return null;
  },
});



// WEBPACK FOOTER //
// ./src/utils/data.js